import React, { useContext, useState, useEffect, useCallback } from "react";
import { TranslatorContext } from "../../context/Translator";
import { Link, useLocation } from "react-router-dom";
import { Row, Col, Spinner, Alert, Form } from "react-bootstrap";
import axios from "axios";
import Select from "react-select";
import { FloatCardComponent } from "../../components/cards";
import IssuerProductsTableComponent from "../../components/tables/products/IssuerProductsTableComponent";
import PageLayout from "../../layouts/PageLayout";

export default function IssuerProductListPage() {
  const { t, n } = useContext(TranslatorContext);
  const location = useLocation();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState("new");

  const [allProducts, setAllProducts] = useState([]);
  const [productsList, setProductsList] = useState([]);

  const [listersOptions, setListersOptions] = useState([]);
  const [mplacersOptions, setMplacersOptions] = useState([]);
  const [categoriesOptions, setCategoriesOptions] = useState([]);
  const [storesOptions, setStoresOptions] = useState([]);

  const [selectedLister, setSelectedLister] = useState("");
  const [selectedMplacer, setSelectedMplacer] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [selectedStore, setSelectedStore] = useState("");
  const [search, setSearch] = useState("");

  const [counts, setCounts] = useState({
    new_products: 0,
    issued_products: 0,
    approved_products: 0,
  });

  const tableHeaders = [
    "Date",
    "Product Code",
    "Title",
    "Selling Price $",
    "Action",
  ];

  const getAuthHeader = () => ({
    headers: {
      Authorization: `Bearer ${localStorage.getItem("authToken")}`,
    },
  });

  const extractProducts = (data) => {
    if (!data) return [];
    if (Array.isArray(data)) {
      if (data.length > 0 && data[0].lister_products) {
        let flattened = [];
        data.forEach((item) => {
          const lister = item.lister;
          const marketPlacer = item.marketPlacer || item.marketplacer;
          const store = item.store;
          (item.lister_products || []).forEach((product) => {
            flattened.push({
              ...product,
              lister_id: lister?.id || product?.lister_id || product?.lister?.id,
              lister_name: lister?.name || product?.lister?.name,
              mplacer_id: marketPlacer?.id || product?.marketPlacer?.id || product?.marketplacer_id || product?.mplacer_id,
              marketPlacer: marketPlacer || product?.marketPlacer,
              store_id: store?.id || product?.store_id || product?.store?.id,
              store: store || product?.store,
            });
          });
        });
        return flattened;
      }
      return data;
    }
    return [];
  };

  const fetchInitialData = useCallback(async () => {
    try {
      const baseUrl = process.env.REACT_APP_API_URL;
      const response = await axios.get(`${baseUrl}/issuer/products/initial-data`, getAuthHeader());
      const resData = response.data?.data || response.data;

      if (resData?.listers) setListersOptions(resData.listers);
      if (resData?.mplacers) setMplacersOptions(resData.mplacers);
      if (resData?.categories) setCategoriesOptions(resData.categories);
      if (resData?.stores) setStoresOptions(resData.stores);
    } catch (err) {
      console.error("Error fetching initial data filters:", err);
    }
  }, []);

  const fetchCounts = useCallback(async () => {
    try {
      const baseUrl = process.env.REACT_APP_API_URL;
      const [newRes, issuedRes, approvedRes] = await Promise.all([
        axios.get(`${baseUrl}/issuer/products`, getAuthHeader()),
        axios.get(`${baseUrl}/issuer/issued-products`, getAuthHeader()),
        axios.get(`${baseUrl}/issuer/checeker-approved-products`, getAuthHeader()),
      ]);

      setCounts({
        new_products: extractProducts(newRes.data?.data).length,
        issued_products: extractProducts(issuedRes.data?.data).length,
        approved_products: extractProducts(approvedRes.data?.data).length,
      });
    } catch (err) {
      console.error("Error fetching counts:", err);
    }
  }, []);

  const fetchTabData = useCallback(async (tab) => {
    setLoading(true);
    setError(null);

    let endpoint = "";
    if (tab === "new") endpoint = "/issuer/products";
    else if (tab === "issued") endpoint = "/issuer/issued-products";
    else if (tab === "approved") endpoint = "/issuer/checeker-approved-products"; // <-- Check karein ye endpoint theek hai

    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}${endpoint}`,
        getAuthHeader()
      );

      const resData = response.data;
      if (!resData.success && resData.success !== undefined) {
        setError(resData.message || "Failed to load products.");
        setAllProducts([]);
        setProductsList([]);
        return;
      }

      const products = extractProducts(resData.data || resData);
      setAllProducts(products);
      setProductsList(products);
    } catch (err) {
      console.error("Error fetching tab data:", err);
      setError(
        err.response?.data?.message || "Failed to fetch data. Please try again."
      );
      setAllProducts([]);
      setProductsList([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const tabParam = queryParams.get("tab");
    if (tabParam && ["new", "issued", "approved"].includes(tabParam)) {
      setActiveTab(tabParam);
    }
  }, [location.search]);

  useEffect(() => {
    fetchInitialData();
    fetchCounts();
  }, [fetchInitialData, fetchCounts]);

  useEffect(() => {
    fetchTabData(activeTab);
  }, [activeTab, fetchTabData]);

  useEffect(() => {
    let filtered = [...allProducts];

    if (search.trim() !== "") {
      const query = search.trim().toLowerCase();
      filtered = filtered.filter(
        (p) =>
          p.title?.toLowerCase().includes(query) ||
          p.product_code?.toLowerCase().includes(query)
      );
    }

    if (selectedLister) {
      filtered = filtered.filter(
        (p) => String(p.lister_id || p.user_id || p.lister?.id || p.lister?.nickname) === String(selectedLister)
      );
    }

    if (selectedMplacer) {
      filtered = filtered.filter((p) => {
        const pMplacerId =
          p.mplacer_id ||
          p.marketplacer_id ||
          p.marketPlacer?.id ||
          p.marketplacer?.id ||
          p.mplacer?.id;

        return String(pMplacerId) === String(selectedMplacer);
      });
    }

    if (selectedCategory) {
      filtered = filtered.filter(
        (p) => String(p.category_id || p.category?.id) === String(selectedCategory)
      );
    }
    if (selectedStore) {
      filtered = filtered.filter(
        (p) => String(p.store_id || p.store?.id) === String(selectedStore)
      );
    }

    setProductsList(filtered);
  }, [allProducts, search, selectedLister, selectedMplacer, selectedCategory, selectedStore]);

  const cardsData = [
    {
      key: "new",
      title: "new product count",
      digit: counts.new_products,
      icon: "add_shopping_cart",
      variant: activeTab === "new" ? "lg blue active-card" : "lg blue",
    },
    {
      key: "issued",
      title: "issued products",
      digit: counts.issued_products,
      icon: "send",
      variant: activeTab === "issued" ? "lg purple active-card" : "lg purple",
    },
    {
      key: "approved",
      title: "Lister Approved Products",
      digit: counts.approved_products,
      icon: "check_circle",
      variant: activeTab === "approved" ? "lg green active-card" : "lg green",
    },
  ];

  const formattedListerOptions = listersOptions.map((l) => ({
    value: String(l.id),
    label: `${l.name || l.username || `Lister ${l.id}`} ${l.nickname ? `(${l.nickname})` : ""}`,
    searchText: `${l.name || ""} ${l.username || ""} ${l.nickname || ""}`.toLowerCase(),
  }));

  const formattedMplacerOptions = mplacersOptions.map((m) => ({
    value: String(m.id),
    label: `${m.name || m.username || `Mplacer ${m.id}`} ${m.nickname ? `(${m.nickname})` : ""}`,
    searchText: `${m.name || ""} ${m.username || ""} ${m.nickname || ""}`.toLowerCase(),
  }));

  const formattedCategoryOptions = categoriesOptions.map((c) => ({
    value: String(c.id),
    label: c.name || c.title,
  }));

  const formattedStoreOptions = storesOptions.map((s) => ({
    value: String(s.id),
    label: s.name || s.store_name,
  }));

  return (
    <PageLayout>
      <Row>
        <Col xl={12}>
          <div className="mc-card">
            <div className="mc-breadcrumb">
              <h3 className="mc-breadcrumb-title">{t("issuer product list")}</h3>
              <ul className="mc-breadcrumb-list">
                <li className="mc-breadcrumb-item">
                  <Link to="#" className="mc-breadcrumb-link">
                    {t("home")}
                  </Link>
                </li>
                <li className="mc-breadcrumb-item">
                  <Link to="#" className="mc-breadcrumb-link">
                    {t("products")}
                  </Link>
                </li>
                <li className="mc-breadcrumb-item">{t("issuer products")}</li>
              </ul>
            </div>
          </div>
        </Col>

        {cardsData.map((card) => (
          <Col key={card.key} sm={6} lg={4}>
            <div
              style={{ cursor: "pointer" }}
              onClick={() => {
                setActiveTab(card.key);
                // URL ko bhi update karein taaki tab loss na ho
                window.history.pushState({}, "", `/issue-product-list?tab=${card.key}`);
              }}
            >
              <FloatCardComponent
                variant={card.variant}
                digit={n(card.digit)}
                title={t(card.title)}
                icon={card.icon}
              />
            </div>
          </Col>
        ))}

        <Col xl={12}>
          <div className="mc-card">
            {error && (
              <Alert variant="danger" className="mb-4">
                {error}
              </Alert>
            )}

            <Row className="mb-4">
              <Col xs={12} sm={6} md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>{t("search")}</Form.Label>
                  <Form.Control
                    type="search"
                    placeholder="Search by title or product code"
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                  />
                </Form.Group>
              </Col>

              <Col xs={12} sm={6} md={2}>
                <Form.Group className="mb-3">
                  <Form.Label>{t("lister by")}</Form.Label>
                  <Select
                    isClearable
                    isSearchable
                    placeholder="All Listers"
                    options={formattedListerOptions}
                    filterOption={(option, inputValue) =>
                      option.data.searchText.includes(inputValue.toLowerCase())
                    }
                    value={
                      formattedListerOptions.find(
                        (opt) => opt.value === String(selectedLister)
                      ) || null
                    }
                    onChange={(opt) => setSelectedLister(opt ? opt.value : "")}
                  />
                </Form.Group>
              </Col>

              <Col xs={12} sm={6} md={2}>
                <Form.Group className="mb-3">
                  <Form.Label>{t("Mplacer by")}</Form.Label>
                  <Select
                    isClearable
                    isSearchable
                    placeholder="MarketPlacers"
                    options={formattedMplacerOptions}
                    filterOption={(option, inputValue) =>
                      option.data.searchText.includes(inputValue.toLowerCase())
                    }
                    value={
                      formattedMplacerOptions.find(
                        (opt) => opt.value === String(selectedMplacer)
                      ) || null
                    }
                    onChange={(opt) => setSelectedMplacer(opt ? opt.value : "")}
                  />
                </Form.Group>
              </Col>

              <Col xs={12} sm={6} md={2}>
                <Form.Group className="mb-3">
                  <Form.Label>{t("category_by")}</Form.Label>
                  <Select
                    isClearable
                    isSearchable
                    placeholder="Categories"
                    options={formattedCategoryOptions}
                    value={
                      formattedCategoryOptions.find(
                        (opt) => opt.value === String(selectedCategory)
                      ) || null
                    }
                    onChange={(opt) => setSelectedCategory(opt ? opt.value : "")}
                  />
                </Form.Group>
              </Col>

              <Col xs={12} sm={6} md={2}>
                <Form.Group className="mb-3">
                  <Form.Label>{t("store by")}</Form.Label>
                  <Select
                    isClearable
                    isSearchable
                    placeholder="All Stores"
                    options={formattedStoreOptions}
                    value={
                      formattedStoreOptions.find(
                        (opt) => opt.value === String(selectedStore)
                      ) || null
                    }
                    onChange={(opt) => setSelectedStore(opt ? opt.value : "")}
                  />
                </Form.Group>
              </Col>

              <Col xl={12}>
                {loading ? (
                  <div className="text-center py-5">
                    <Spinner animation="border" variant="primary" />
                    <p className="mt-2 text-muted">{t("loading_products...")}</p>
                  </div>
                ) : (
                  <IssuerProductsTableComponent
                    thead={tableHeaders}
                    tbody={productsList}
                    activeTab={activeTab}
                  />
                )}
              </Col>
            </Row>
          </div>
        </Col>
      </Row>
    </PageLayout>
  );
}